/** Small wall fuse box at Rosalie's boot-height, with later animation pivots intact. */
import * as THREE from 'three';
import { getZone, type ZonePalette } from '@/render/palette';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const zone: ZonePalette = (() => {
  const found = getZone('fattening');
  if (!found) throw new Error('fuse-box: fattening palette missing');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`fuse-box: fattening surface ${index} missing`);
  return colour;
}

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'fuse-box';
  const paintedSteel = surface('metal', zoneSurface(2));
  const shadowSteel = surface('metal', zoneSurface(0));

  // The cabinet itself is deliberately shallow: the lid edge, hinge and lever
  // make the 64 px read, while a deeper box would become a shelf on the wall.
  const cabinet = part(new THREE.BoxGeometry(0.28, 0.35, 0.098), paintedSteel, 'fuse-box-cabinet');
  cabinet.position.set(0, 0.175, -0.006);
  group.add(cabinet);

  const lidPivot = new THREE.Group();
  lidPivot.name = 'fuse-box-lid-pivot';
  // This is the left hinge line. Keeping the closed transform at identity makes
  // the later nudge director own the entire opening rather than undoing a kit pose.
  lidPivot.position.set(-0.13, 0.175, 0.043);
  const lid = part(new THREE.BoxGeometry(0.26, 0.31, 0.012), paintedSteel, 'fuse-box-lid');
  lid.position.set(0.13, 0, 0.006);
  lidPivot.add(lid);
  group.add(lidPivot);

  const hinges = new THREE.Group();
  hinges.name = 'fuse-box-hinges';
  for (const [y, name] of [[0.105, 'fuse-box-hinge-lower'], [0.245, 'fuse-box-hinge-upper']] as const) {
    // The first 50 mm hinge barrel quietly made the entire kit 132 mm deep.
    // This 20 mm pin still reads as hardware beside the lid, while keeping the
    // quoted 110 mm cabinet depth honest for room placement and asset framing.
    const hinge = part(new THREE.CylinderGeometry(0.012, 0.012, 0.02, 6), shadowSteel, name);
    hinge.rotation.x = Math.PI / 2;
    hinge.position.set(-0.14, y, 0.045);
    hinges.add(hinge);
  }
  group.add(hinges);

  const leverPivot = new THREE.Group();
  leverPivot.name = 'fuse-box-lever-pivot';
  // Its y is the stated boot-height: a small handle can be nudged at Rosalie's
  // eyeline without moving the whole cabinet to a human wall height.
  leverPivot.position.set(0.065, 0.30, 0.045);
  const leverBase = part(new THREE.CylinderGeometry(0.025, 0.025, 0.012, 8), shadowSteel, 'fuse-box-lever-base');
  leverBase.rotation.x = Math.PI / 2;
  leverPivot.add(leverBase);
  const lever = part(new THREE.BoxGeometry(0.075, 0.018, 0.016), shadowSteel, 'fuse-box-lever-handle');
  lever.position.set(0.022, 0, 0.002);
  leverPivot.add(lever);
  group.add(leverPivot);

  return group;
}

export const fuseBoxAsset: AssetDefinition = {
  id: 'fuse-box',
  label: 'Fuse box',
  description: 'Shallow painted-steel fuse box with hinged lid and boot-height external lever.',
  footprint: { width: 0.28, height: 0.35, length: 0.11 },
  create: build,
};
